import React, { useEffect, useState } from 'react';
import { getYouTubeEmbedUrl } from '../../utils/youtube';

interface Feature {
    icon: React.ComponentType<{ className?: string }>;
    iconBg: string;
    iconColor: string;
    title: string;
    description: string;
}

interface OnboardingSlideProps {
    title: string;
    subtitle: string;
    videoUrl: string;
    features: Feature[];
}

const OnboardingSlide: React.FC<OnboardingSlideProps> = ({
    title,
    subtitle,
    videoUrl,
    features
}) => {
    const [stage, setStage] = useState(0);
    const embedUrl = getYouTubeEmbedUrl(videoUrl);

    useEffect(() => {
        setStage(0);
        const t1 = setTimeout(() => setStage(1), 100);
        const t2 = setTimeout(() => setStage(2), 450);
        const t3 = setTimeout(() => setStage(3), 800);
        const featureTimers = features.map((_, i) =>
            setTimeout(() => setStage(4 + i), 1100 + i * 200)
        );
        return () => {
            clearTimeout(t1);
            clearTimeout(t2);
            clearTimeout(t3);
            featureTimers.forEach(clearTimeout);
        };
    }, [title]);

    return (
        <div className="flex flex-col items-center w-full max-w-5xl mx-auto px-8 overflow-y-auto scrollbar-hide">
            {/* Title */}
            <h2
                className="text-4xl font-bold text-gray-900 tracking-tight text-center transition-all duration-700 ease-out"
                style={{
                    opacity: stage >= 1 ? 1 : 0,
                    transform: stage >= 1 ? 'translateY(0)' : 'translateY(24px)'
                }}
            >
                {title}
            </h2>

            {/* Subtitle */}
            <p
                className="text-base text-gray-400 text-center mt-3 whitespace-nowrap leading-relaxed transition-all duration-700 ease-out"
                style={{
                    opacity: stage >= 2 ? 1 : 0,
                    transform: stage >= 2 ? 'translateY(0)' : 'translateY(16px)'
                }}
            >
                {subtitle}
            </p>

            {/* Video */}
            <div
                className="w-full mt-8 rounded-2xl overflow-hidden bg-gray-900 shadow-lg transition-all duration-700 ease-out"
                style={{
                    aspectRatio: '16 / 9',
                    opacity: stage >= 3 ? 1 : 0,
                    transform:
                        stage >= 3 ? 'translateY(0) scale(1)' : 'translateY(20px) scale(0.97)'
                }}
            >
                {embedUrl ? (
                    <iframe
                        width="100%"
                        height="100%"
                        src={embedUrl}
                        title={title}
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                        className="w-full h-full"
                    />
                ) : (
                    <div className="w-full h-full flex items-center justify-center text-gray-400 text-sm">
                        Video coming soon
                    </div>
                )}
            </div>

            {/* Feature cards */}
            <div className="flex gap-5 mt-8 pb-4 w-full">
                {features.map((feature, i) => (
                    <div
                        key={feature.title}
                        className="flex-1 flex flex-col items-center text-center p-5 rounded-2xl bg-gray-50 border border-gray-100 transition-all duration-700 ease-out"
                        style={{
                            opacity: stage >= 4 + i ? 1 : 0,
                            transform: stage >= 4 + i ? 'translateY(0)' : 'translateY(24px)'
                        }}
                    >
                        <div
                            className={`flex items-center justify-center w-10 h-10 rounded-xl ${feature.iconBg}`}
                        >
                            <feature.icon className={`w-5 h-5 ${feature.iconColor}`} />
                        </div>
                        <span className="text-sm font-semibold text-gray-900 mt-3">
                            {feature.title}
                        </span>
                        <p className="text-xs text-gray-400 leading-relaxed mt-1.5">
                            {feature.description}
                        </p>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default OnboardingSlide;
